import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { issueAppEntryPoint } from '@jira/issue__issue-view/src/issueAppEntryPoint.entrypoint.tsx';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { expVal } from '@atlassian/jira-feature-experiments';
import { workItemLazyQueryWrapperEntryPoint } from '@jira/work-item__work-item-query-wrapper/src/workItemLazyQueryWrapperEntryPoint.entrypoint.tsx';
import type {
	IssueAppSelectorEntrypointProps,
	IssueAppSelectorEntryPointExtraProps,
	IssueAppSelectorEntryPointParams,
	IssueAppSelectorEntryPointQueries,
	IssueAppSelectorNestedEntryPoints,
} from './IssueAppSelectorEntryPoint.entrypoint.types.tsx';
import issueAppSelectorOrchestratorQueryParameters from './__generated__/IssueAppSelectorOrchestratorQuery$parameters.ts';

type IssueAppSelectorEntryPointComponent = EntryPointComponent<
	IssueAppSelectorEntryPointQueries,
	IssueAppSelectorNestedEntryPoints,
	IssueAppSelectorEntrypointProps,
	IssueAppSelectorEntryPointExtraProps
>;

export const issueAppSelectorEntryPoint: EntryPoint<
	IssueAppSelectorEntryPointComponent,
	IssueAppSelectorEntryPointParams
> = createEntryPoint({
	root: JSResourceForUserVisible<IssueAppSelectorEntryPointComponent>(
		/* webpackChunkName: "async-issue-app-selector-orchestrator" */ './IssueAppSelectorOrchestrator.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps: ({
		issueViewVariant,
		issueKey,
		cloudId,
		isAnonymous,
		isInIssuePreviewPanel,
	}: IssueAppSelectorEntryPointParams) => {
		const preloadProps = {
			queries: {
				issueAppSelectorQuery: {
					parameters: issueAppSelectorOrchestratorQueryParameters,
					variables: {
						issueKey,
						cloudId,
						includeAgenticWorkItemData: expVal('work_item_for_quadratic', 'isEnabled', false),
					},
					options: { fetchPolicy: 'store-or-network' as const },
				},
			},
			extraProps: {
				issueViewVariantHint: issueViewVariant,
			},
		};

		if (issueViewVariant === 'modern') {
			return {
				...preloadProps,
				entryPoints: {
					workItemLazyQueryWrapper: {
						entryPoint: workItemLazyQueryWrapperEntryPoint,
						entryPointParams: {
							issueKey,
							cloudId,
							isAnonymous,
							isInIssuePreviewPanel,
						},
					},
				},
			};
		}

		if (issueViewVariant === 'classic') {
			return {
				...preloadProps,
				entryPoints: {
					issueApp: {
						entryPoint: issueAppEntryPoint,
						entryPointParams: {
							querySource: 'entrypoint' as const,
							issueKey,
							cloudId,
						},
					},
				},
			};
		}

		return preloadProps;
	},
});
